import React from "react";
import { ClipboardCheck, ArrowRight, Sparkles, Zap, TrendingUp } from "lucide-react";

/**
 * DiagnosticCTA — replaces the old e-book LeadMagnet form on the landing.
 * The diagnostic form itself lives at innovia.com.br and already handles
 * HubSpot capture, so this section is pure marketing copy + outbound CTA.
 */

const DIAGNOSTIC_URL = "https://lp1.innovia.com.br/diagnostico-inteligente-smartstockai.html";
const UTM_PARAMS = "utm_source=smartstock_landing&utm_medium=cta&utm_campaign=diagnostico_estoque";

export const diagnosticLink = () => {
  const separator = DIAGNOSTIC_URL.includes("?") ? "&" : "?";
  return `${DIAGNOSTIC_URL}${separator}${UTM_PARAMS}`;
};

const BULLETS = [
  "Identifique risco de ruptura em produtos-chave",
  "Descubra capital parado em SKUs sem giro",
  "Avalie a maturidade da sua previsão de demanda",
  "Entenda se sua compra é baseada em dados ou feeling",
  "Receba um score e recomendações práticas",
];

const BADGES = [
  { icon: Sparkles, label: "Gratuito" },
  { icon: Zap, label: "Leva poucos minutos" },
  { icon: TrendingUp, label: "Score imediato" },
];

export default function DiagnosticCTA() {
  return (
    <div
      className="rounded-2xl border border-primary/30 bg-card overflow-hidden shadow-[0_0_40px_-10px_rgba(240,90,63,0.35)]"
      data-testid="diagnostic-cta-section"
    >
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-0">
        {/* Visual side */}
        <div className="relative bg-gradient-to-br from-primary/15 via-transparent to-transparent p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-border flex items-center">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="relative">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/15 border border-primary/30 text-xs font-medium text-primary uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" /> Diagnóstico gratuito
            </span>
            <h3 className="font-display text-3xl lg:text-4xl font-bold mt-4 leading-tight">
              Descubra a saúde do estoque do seu varejo
            </h3>
            <p className="text-muted-foreground mt-4 text-sm lg:text-base leading-relaxed">
              Em poucos minutos, veja se sua operação está vulnerável a ruptura, estoque parado ou compras no feeling.
            </p>

            <div className="mt-6 grid grid-cols-3 gap-2">
              {BADGES.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-secondary/40 border border-border text-center"
                >
                  <Icon className="w-4 h-4 text-primary" />
                  <span className="text-[10px] lg:text-xs text-muted-foreground font-medium leading-tight">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content side */}
        <div className="p-8 lg:p-10 flex flex-col justify-center">
          <div className="flex items-center gap-2 text-primary mb-3">
            <ClipboardCheck className="w-5 h-5" />
            <span className="text-xs font-semibold uppercase tracking-wider">O que você vai descobrir</span>
          </div>

          <ul className="space-y-3 text-sm lg:text-base text-foreground/90">
            {BULLETS.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <p className="mt-5 text-xs text-muted-foreground italic">
            Ao final, você recebe um score de maturidade e recomendações práticas para melhorar sua operação.
          </p>

          <div className="mt-6">
            <a
              href={diagnosticLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="diagnostic-cta-btn"
              className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto h-12 px-6 rounded-lg bg-primary hover:bg-primary/90 text-white font-semibold transition-all shadow-lg shadow-primary/25 hover:shadow-primary/40"
            >
              Fazer diagnóstico gratuito
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <p className="mt-3 text-xs text-muted-foreground">
              Gratuito · Leva poucos minutos · Resultado imediato
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
